import { UsafFillFlag } from '../enums/usaf-fill-flag.enum'
import { UsafOrderSource } from '../enums/usaf-order-source.enum'
import { UsafAutoOrderTrigger } from '../enums/usaf-auto-order.enum'

// A USAF sales order can be split-invoiced — one entry per invoice or credit memo.
export interface IUsafOrderInvoice {
  invoiceNumber: string
  invoiceDate?: string
  totalCost: number
  coreTotal: number
  // paid, unpaid, ssp, refunded, pending — from InvoiceStatusDetail
  invoiceStatus?: string
  amountDue?: number
  isCredit?: boolean
}

export interface IUsafOrderLine {
  id: string
  lineNumber: number
  lineCode: string
  manufacturer: string
  partNumber: string
  description: string
  quantityRequested: number
  quantityShipped: number
  quantityBackOrdered: number
  list: number
  cost: number
  core: number
  fet: number
  fee: number
  tax: number
  errorCode?: string
  errorMessage?: string
}

// Protractor invoice whose sale triggered an automatic order; absent for manual orders.
// What placed an automatic order and how much of the need it covers.
export interface IUsafOrderAutoTrigger {
  trigger: UsafAutoOrderTrigger
  // Invoice trigger only.
  invoiceId?: string
  invoiceNumber?: number
  // Refill triggers only.
  runDate?: string
  // This order's quantity, out of `need`: the line's sold quantity or the part's Net to Order.
  quantity: number
  need: number
}

export interface IUsafOrder {
  id: string
  transactionId: string
  locationId: string
  userId?: string
  userName?: string
  accountNumber?: string
  branch?: string
  branchName?: string
  orderReference: string
  hdsOrderNumber: string
  soNumber?: string
  invoices?: IUsafOrderInvoice[]
  // Automatic orders only.
  autoOrder?: IUsafOrderAutoTrigger
  poNumber: string
  fillFlag?: UsafFillFlag
  source?: UsafOrderSource
  shipMethod?: string
  shipAddress1?: string
  status: string
  errorCode?: string
  errorMessage?: string
  orderDate: string
  deliveryDate?: string
  created?: string
  deliveryCost?: number
  tax?: number
  tpsTax?: number
  totalFET?: number
  comment: string
  ccSurchargeAmount?: number
  totalCost?: number
  coreTotal?: number
  lines: IUsafOrderLine[]
}

export interface IUsafSubmitOrderLine {
  lineCode: string
  partNumber: string
  manufacturer: string
  description: string
  quantityRequested: number
}

export interface IUsafSubmitOrderRequest {
  locationId: string
  fillFlag: UsafFillFlag
  comment: string
  branch?: string
  branchName?: string
  lines: IUsafSubmitOrderLine[]
}

export interface IUsafOrderHistoryResponse {
  orders: IUsafOrder[]
}

export type IUsafSubmitOrderResponse = IUsafOrder

export interface IUsafStockCheckBranch {
  code: string
  name: string
  deliveryDate: string | null
  transferRequired: boolean
  cutoffDateTime: string | null
}

export interface IUsafStockCheckBranchAvailability {
  branchCode: string
  quantityAvailable: number
}

export interface IUsafStockCheckResult {
  price: number
  availableByBranch: Record<string, number>
}

export interface IUsafStockCheckPartRequest {
  lineCode: string
  partNumber: string
  qty: number
}

export interface IUsafStockCheckRequest {
  locationId: string
  parts: IUsafStockCheckPartRequest[]
}

export interface IUsafStockCheckItem {
  partNumber: string
  cost: number | null
  branches: IUsafStockCheckBranchAvailability[]
}

export interface IUsafStockCheckResponse {
  branches: IUsafStockCheckBranch[]
  items: IUsafStockCheckItem[]
  failed: boolean
}

// Raised by CheckUsafStock when USAF itself failed (the API answers 200 with `failed`),
// so the page can tell it apart from HTTP errors the interceptor already toasts.
export class UsafStockCheckFailedError extends Error {
  constructor() {
    super('USAF stock check failed')
  }
}

export interface IUsafStockCheckLineRequest {
  lineId: string
  lineCode: string
  partNumber: string
  qty: number
}

// crypto.randomUUID is not available on insecure origins or older browsers.
// Fall back to a time + random string so IDs stay unique across a session.
export function generateLineId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined
  if (c?.randomUUID) return c.randomUUID()
  return `line-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function getBranchAvailability(
  stock: IUsafStockCheckResult | undefined,
  branchCode: string | null | undefined
): number | undefined {
  if (!stock || !branchCode) return undefined
  return stock.availableByBranch[branchCode]
}

export function isLineUnderStocked(
  line: IUsafOrderLine,
  stockByLineId: Record<string, IUsafStockCheckResult>,
  branchCode: string | null | undefined
): boolean {
  const stock = stockByLineId[line.id]
  if (!stock || !branchCode) return false
  const available = stock.availableByBranch[branchCode]
  if (available == null) return false
  return available < line.quantityRequested
}
